import React, { useState, useEffect } from 'react';
import { Button, Icon } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { AttachmentList } from '@/components/organisms/Attachment/AttachmentList';
import { CreateProductVariantSchema, type CreateProductVariantRequest } from '@/schemas/variants';
import {
  useCreateProductVariantMutation,
  useUpdateProductVariantMutation,
  useCollaboratorsList,
} from '@/hooks/useErpApi';
import type { ProductVariantResponse, UpdateProductVariantRequest } from '@kisanlink/erp-service';

interface ProductVariantFormProps {
  productId?: string;
  variantId?: string;
  initialData?: ProductVariantResponse;
  collaboratorId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const ProductVariantForm: React.FC<ProductVariantFormProps> = ({
  productId: initialProductId,
  variantId,
  initialData,
  collaboratorId: initialCollaboratorId,
  onSuccess,
  onCancel,
}) => {
  const isEditMode = !!variantId && !!initialData;
  const { mutate: createVariant, isLoading: isCreating } = useCreateProductVariantMutation();
  const { mutateAsync: updateVariant, isLoading: isUpdating } = useUpdateProductVariantMutation();
  const { data: collaborators, isLoading: collaboratorsLoading } = useCollaboratorsList();
  
  const isLoading = isCreating || isUpdating;

  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<CreateProductVariantRequest>({
    variant_name: initialData?.variant_name || '',
    quantity: initialData?.quantity || '',
    pack_size: initialData?.pack_size || '',
    collaborator_ids: initialData?.collaborator_ids || (initialCollaboratorId ? [initialCollaboratorId] : []),
    brand_name: initialData?.brand_name || '',
    hsn_code: initialData?.hsn_code || '',
    gst_rate: initialData?.gst_rate || 0,
    prices: initialData?.prices?.map(p => ({
      price_type: p.price_type,
      price: p.price,
      currency: p.currency || 'INR',
      effective_from: p.effective_from,
      effective_to: p.effective_to,
      is_active: p.is_active,
    })) || [],
    images: initialData?.images || [],
    dosage_instructions: initialData?.dosage_instructions || '',
    usage_details: initialData?.usage_details || '',
  });

  const [selectedCollaboratorIds, setSelectedCollaboratorIds] = useState<string[]>(
    initialData?.collaborator_ids || (initialCollaboratorId ? [initialCollaboratorId] : [])
  );

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        variant_name: initialData.variant_name || '',
        quantity: initialData.quantity || '',
        pack_size: initialData.pack_size || '',
        collaborator_ids: initialData.collaborator_ids || [],
        brand_name: initialData.brand_name || '',
        hsn_code: initialData.hsn_code || '',
        gst_rate: initialData.gst_rate || 0,
        prices: initialData.prices?.map(p => ({
          price_type: p.price_type,
          price: p.price,
          currency: p.currency || 'INR',
          effective_from: p.effective_from,
          effective_to: p.effective_to,
          is_active: p.is_active,
        })) || [],
        images: initialData.images || [],
        dosage_instructions: initialData.dosage_instructions || '',
        usage_details: initialData.usage_details || '',
      });
      setSelectedCollaboratorIds(initialData.collaborator_ids || []);
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let processedValue: string | number = value;

    // Convert gst_rate to number
    if (name === 'gst_rate') {
      processedValue = value === '' ? 0 : parseFloat(value);
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    // Validate collaborator selection
    if (selectedCollaboratorIds.length === 0) {
      setError('Please select at least one supplier');
      return;
    }

    // Validate product is selected
    if (!initialProductId) {
      setError('Product ID is required');
      return;
    }

    try {
      // Normalize price_type values: MRP and MSP must be uppercase, member and retail can be lowercase
      console.log('[ProductVariantForm] Original prices before normalization:', formData.prices);
      
      const normalizedPrices = formData.prices?.map((price, idx) => {
        if (!price.price_type) {
          console.warn(`[ProductVariantForm] Price at index ${idx} has no price_type, defaulting to MRP`);
          return { ...price, price_type: 'MRP' };
        }
        
        const priceType = String(price.price_type).trim();
        const priceTypeLower = priceType.toLowerCase();
        let normalizedType: string;
        
        // Normalize to uppercase for MRP and MSP, keep member and retail as lowercase
        if (priceTypeLower === 'msp') {
          normalizedType = 'MSP';
        } else if (priceTypeLower === 'mrp') {
          normalizedType = 'MRP';
        } else if (priceTypeLower === 'member') {
          normalizedType = 'member';
        } else if (priceTypeLower === 'retail') {
          normalizedType = 'retail';
        } else {
          // If already in correct format, use as-is
          normalizedType = priceType;
        }
        
        // Debug log to verify normalization
        if (priceType !== normalizedType) {
          console.log(`[ProductVariantForm] Normalized price_type at index ${idx}: "${priceType}" → "${normalizedType}"`);
        }
        
        return {
          ...price,
          price_type: normalizedType,
        };
      });
      
      // Debug: Log final prices before submission
      console.log('[ProductVariantForm] Final normalized prices before submission:', normalizedPrices);

      // Clean formData - remove empty arrays and empty strings for optional fields
      const cleanedData = {
        ...formData,
        collaborator_ids: selectedCollaboratorIds,
        barcode: (formData as any).barcode || undefined,
        description: (formData as any).description || undefined,
        images: formData.images && formData.images.length > 0 ? formData.images : undefined,
        dosage_instructions: formData.dosage_instructions || undefined,
        usage_details: formData.usage_details || undefined,
        prices: normalizedPrices && normalizedPrices.length > 0 ? normalizedPrices : undefined,
      };

      if (isEditMode && variantId) {
        // Update existing variant
        const updateData: UpdateProductVariantRequest = {
          variant_name: cleanedData.variant_name,
          quantity: cleanedData.quantity,
          pack_size: cleanedData.pack_size,
          brand_name: cleanedData.brand_name,
          hsn_code: cleanedData.hsn_code,
          gst_rate: cleanedData.gst_rate,
          barcode: cleanedData.barcode,
          description: cleanedData.description,
          dosage_instructions: cleanedData.dosage_instructions,
          usage_details: cleanedData.usage_details,
          images: cleanedData.images,
          is_active: initialData?.is_active,
        };

        const result = await updateVariant({
          id: variantId,
          data: updateData,
        });

        if (result) {
          onSuccess?.();
        }
      } else {
        // Create new variant
        const validated = CreateProductVariantSchema.parse(cleanedData);

        const result = await createVariant({
          productId: initialProductId!,
          data: validated,
        });

        // Variant created successfully
        if (result) {
          onSuccess?.();
        }
      }
    } catch (err) {
      // Handle Zod validation errors
      if (err && typeof err === 'object' && 'errors' in err) {
        const zodError = err as { errors: Array<{ path: (string | number)[]; message: string }> };
        const errMap: Record<string, string> = {};
        zodError.errors.forEach((e) => {
          // Handle nested paths like prices[0].price_type
          if (e.path.length > 1) {
            // For nested fields, create a key like "prices.0.price_type"
            const field = e.path.join('.');
            errMap[field] = e.message;
          } else {
            const field = String(e.path[0]);
            errMap[field] = e.message;
          }
        });
        console.log('[ProductVariantForm] Validation errors:', errMap);
        setValidationErrors(errMap);
        // Only show the generic error message if there are actual validation errors
        if (Object.keys(errMap).length > 0) {
          setError('Please fix the validation errors below');
        } else {
          setError('Validation failed. Please check the form fields.');
        }
      } else {
        // Handle API errors
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setError(`Failed to create variant: ${errorMessage}`);
      }
    }
  };

  const isFormLoading = isLoading || collaboratorsLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="CircleAlert" size={20} className="text-error-600 flex-shrink-0 mt-0.5" />
            <p className="text-error-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Product ID Info */}
      {!initialProductId && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="CircleAlert" size={20} className="text-error-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-error-700 text-sm font-medium">Product ID Required</p>
              <p className="text-error-600 text-sm mt-1">
                Product ID must be provided to create a variant. Please navigate from a product page
                or provide productId as a prop.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Variant Name, Quantity, Pack Size */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="variant_name" className="block text-sm font-medium text-neutral-700 mb-2">
            Variant Name <span className="text-error-600">*</span>
          </label>
          <Input
            id="variant_name"
            name="variant_name"
            value={formData.variant_name}
            onChange={handleChange}
            placeholder="e.g., 500g"
            disabled={isFormLoading}
          />
          {validationErrors.variant_name && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.variant_name}</p>
          )}
        </div>
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-neutral-700 mb-2">
            Quantity <span className="text-error-600">*</span>
          </label>
          <Input
            id="quantity"
            name="quantity"
            value={formData.quantity}
            onChange={handleChange}
            placeholder="e.g., 500g"
            disabled={isFormLoading}
          />
          {validationErrors.quantity && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.quantity}</p>
          )}
        </div>
        <div>
          <label htmlFor="pack_size" className="block text-sm font-medium text-neutral-700 mb-2">
            Pack Size <span className="text-error-600">*</span>
          </label>
          <Input
            id="pack_size"
            name="pack_size"
            value={formData.pack_size}
            onChange={handleChange}
            placeholder="e.g., Single"
            disabled={isFormLoading}
          />
          {validationErrors.pack_size && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.pack_size}</p>
          )}
        </div>
      </div>

      {/* Supplier Selection (Multi-select) */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Suppliers <span className="text-error-600">*</span>
        </label>
        
        <div className={`border rounded-lg max-h-[200px] overflow-y-auto p-2 space-y-1 ${validationErrors.collaborator_ids ? 'border-error-300' : 'border-neutral-300'}`}>
          {(collaborators || []).map((collaborator) => (
            <label key={collaborator.id} className="flex items-center gap-2 p-2 rounded hover:bg-neutral-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedCollaboratorIds.includes(collaborator.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    const newSelected = [...selectedCollaboratorIds, collaborator.id];
                    setSelectedCollaboratorIds(newSelected);
                    // Clear validation error
                    if (validationErrors.collaborator_ids) {
                      setValidationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.collaborator_ids;
                        return newErrors;
                      });
                    }
                  } else {
                    setSelectedCollaboratorIds(selectedCollaboratorIds.filter(id => id !== collaborator.id));
                  }
                }}
                disabled={isFormLoading || !!initialCollaboratorId}
                className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-neutral-700">{collaborator.company_name}</span>
            </label>
          ))}
          {(!collaborators || collaborators.length === 0) && (
            <p className="text-sm text-neutral-500 p-2 text-center">No suppliers available</p>
          )}
        </div>

        {validationErrors.collaborator_ids && (
          <p className="text-error-600 text-sm mt-1">{validationErrors.collaborator_ids}</p>
        )}
        {!validationErrors.collaborator_ids && selectedCollaboratorIds.length === 0 && (
          <p className="text-neutral-600 text-xs mt-1">
            Select one or more suppliers
          </p>
        )}
        {!validationErrors.collaborator_ids && selectedCollaboratorIds.length > 0 && (
          <p className="text-neutral-600 text-xs mt-1">
            {selectedCollaboratorIds.length} supplier(s) selected
          </p>
        )}
      </div>

      {/* Brand Name */}
      <div>
        <label htmlFor="brand_name" className="block text-sm font-medium text-neutral-700 mb-2">
          Brand Name
        </label>
        <Input
          id="brand_name"
          name="brand_name"
          value={formData.brand_name || ''}
          onChange={handleChange}
          placeholder="e.g., ABC Paracetamol Plus"
          disabled={isFormLoading}
        />
        {validationErrors.brand_name && (
          <p className="text-error-600 text-sm mt-1">{validationErrors.brand_name}</p>
        )}
        <p className="text-neutral-600 text-xs mt-1">
          Supplier-specific brand name for this product (optional)
        </p>
      </div>

      {/* HSN Code and GST Rate Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* HSN Code */}
        <div>
          <label htmlFor="hsn_code" className="block text-sm font-medium text-neutral-700 mb-2">
            HSN Code <span className="text-error-600">*</span>
          </label>
          <Input
            id="hsn_code"
            name="hsn_code"
            value={formData.hsn_code}
            onChange={handleChange}
            placeholder="30049011"
            maxLength={8}
            disabled={isFormLoading}
          />
          {validationErrors.hsn_code && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.hsn_code}</p>
          )}
          <p className="text-neutral-600 text-xs mt-1">8-digit HSN code for GST</p>
        </div>

        {/* GST Rate */}
        <div>
          <label htmlFor="gst_rate" className="block text-sm font-medium text-neutral-700 mb-2">
            GST Rate (%) <span className="text-error-600">*</span>
          </label>
          <Input
            id="gst_rate"
            name="gst_rate"
            type="number"
            value={formData.gst_rate}
            onChange={handleChange}
            placeholder="12.0"
            min={0}
            max={100}
            step={0.01}
            disabled={isFormLoading}
          />
          {validationErrors.gst_rate && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.gst_rate}</p>
          )}
          <p className="text-neutral-600 text-xs mt-1">GST percentage (0-100)</p>
        </div>
      </div>

      {/* Prices Section */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">Prices</h3>
            <p className="text-xs text-neutral-600 mt-1">
              Add pricing information (optional - can be added later)
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                prices: [
                  ...(prev.prices || []),
                  { price_type: 'MRP', price: 0, currency: 'INR', is_active: true }, // Keep 0 here as initial value for backend, UI will handle display
                ],
              }));
            }}
            disabled={isFormLoading}
          >
            <Icon name="Plus" size={16} className="mr-1" />
            Add Price
          </Button>
        </div>
        {formData.prices && formData.prices.length > 0 ? (
          <div className="space-y-3">
            {formData.prices.map((price, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 bg-white p-3 rounded border">
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">
                    Price Type
                  </label>
                  <select
                    key={`price-type-${index}-${price.price_type}`}
                    value={price.price_type || 'MRP'}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      
                      // Normalize the value immediately - ensure MSP stays as MSP, not MRP
                      let normalizedValue: string;
                      const valueLower = selectedValue.toLowerCase();
                      
                      if (valueLower === 'msp') {
                        normalizedValue = 'MSP';
                      } else if (valueLower === 'mrp') {
                        normalizedValue = 'MRP';
                      } else if (valueLower === 'member') {
                        normalizedValue = 'member';
                      } else if (valueLower === 'retail') {
                        normalizedValue = 'retail';
                      } else {
                        normalizedValue = selectedValue; // Fallback to original value
                      }
                      
                      // Create a new prices array with the updated value
                      const newPrices = (formData.prices || []).map((p, i) => 
                        i === index ? { ...p, price_type: normalizedValue } : p
                      );
                      
                      setFormData((prev) => ({ ...prev, prices: newPrices }));
                    }}
                    className="w-full px-2 py-1 text-sm border border-neutral-300 rounded"
                  >
                    <option value="MSP">MSP (Maximum Selling Price)</option>
                    <option value="member">Member</option>
                    <option value="retail">Retail</option>
                    <option value="MRP">MRP (Maximum Retail Price)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Price</label>
                  <Input
                    type="number"
                    value={price.price === 0 ? '' : price.price} 
                    onChange={(e) => {
                      const val = e.target.value;
                      const newPrices = [...(formData.prices || [])];
                      newPrices[index] = { ...price, price: val === '' ? 0 : parseFloat(val) };
                      setFormData((prev) => ({ ...prev, prices: newPrices }));
                    }}
                    placeholder="0.00"
                    min={0}
                    step={0.01}
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Currency</label>
                  <Input
                    value={price.currency || 'INR'}
                    onChange={(e) => {
                      const newPrices = [...(formData.prices || [])];
                      newPrices[index] = { ...price, currency: e.target.value };
                      setFormData((prev) => ({ ...prev, prices: newPrices }));
                    }}
                    placeholder="INR"
                    className="text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const newPrices = formData.prices?.filter((_, i) => i !== index) || [];
                      setFormData((prev) => ({ ...prev, prices: newPrices }));
                    }}
                    disabled={isFormLoading}
                  >
                    <Icon name="Trash2" size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-neutral-500 italic">No prices added yet</p>
        )}
      </div>

      {/* Image Upload - Only show in edit mode */}
      {isEditMode && variantId && (
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Variant Images
          </label>
          <AttachmentList
            entityType="variant"
            entityId={variantId}
            allowUpload={true}
            allowDelete={true}
            viewMode="grid"
          />
        </div>
      )}

      {/* Dosage Instructions */}
      <div>
        <label
          htmlFor="dosage_instructions"
          className="block text-sm font-medium text-neutral-700 mb-2"
        >
          Dosage Instructions
        </label>
        <textarea
          id="dosage_instructions"
          name="dosage_instructions"
          value={formData.dosage_instructions}
          onChange={handleChange}
          placeholder="e.g., Take 1-2 tablets every 4-6 hours"
          rows={3}
          disabled={isFormLoading}
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100 disabled:cursor-not-allowed resize-none"
        />
        {validationErrors.dosage_instructions && (
          <p className="text-error-600 text-sm mt-1">{validationErrors.dosage_instructions}</p>
        )}
      </div>

      {/* Usage Details */}
      <div>
        <label htmlFor="usage_details" className="block text-sm font-medium text-neutral-700 mb-2">
          Usage Details
        </label>
        <textarea
          id="usage_details"
          name="usage_details"
          value={formData.usage_details}
          onChange={handleChange}
          placeholder="Additional usage information..."
          rows={3}
          disabled={isFormLoading}
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100 disabled:cursor-not-allowed resize-none"
        />
        {validationErrors.usage_details && (
          <p className="text-error-600 text-sm mt-1">{validationErrors.usage_details}</p>
        )}
      </div>

      {/* Product Images Info */}
      <div className="bg-info-50 border border-info-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-info-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-info-700 text-sm font-medium">Product Images</p>
            <p className="text-info-600 text-sm mt-1">
              Product images can be uploaded after the variant is created. You&apos;ll be able to
              manage images in the variant details page.
            </p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isFormLoading}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isFormLoading}>
          {isFormLoading ? (
            <>
              <Icon name="LoaderCircle" size={16} className="mr-2 animate-spin" />
              {isEditMode ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            <>
              <Icon name={isEditMode ? "Save" : "Plus"} size={16} className="mr-2" />
              {isEditMode ? 'Update Variant' : 'Create Variant'}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};
